import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import styles from './DynamicSizingGuide.module.scss';
import { type BreedWeight } from './BreedDataset';
import { SizeOption } from '../../../../types';
import { F3BandSizeOption, f3LargerSize } from '../../../../lib/size';
import { distributionRows } from './sizeFromDistribution';

interface DynamicSizingGuideResultProps {
  sizingGuideItem: BreedWeight;
  breedName?: string;
  isPuppy: boolean;
  onSelect: (newValue: SizeOption) => void;
  /** Floor from the outcome-based weight bands; see f3MinimumSizeForWeight. */
  minimumSize?: SizeOption;
  /** Whether the floor came from the dog's head/neck geometry, which changes how we explain it. */
  hasWideNeck?: boolean;
}

const sizeLabel = (size: SizeOption, t: (key: string) => string): string => {
  if (size === SizeOption.XLarge) {
    return t('x_large');
  } else if (size === SizeOption.Large) {
    return t('large');
  } else if (size === SizeOption.Medium) {
    return t('medium');
  } else if (size === SizeOption.Small) {
    return t('small');
  } else if (size === SizeOption.XSmall) {
    return t('x_small');
  } else {
    return t('unknown');
  }
};

export default function DynamicSizingGuideResult({
  sizingGuideItem,
  breedName,
  isPuppy,
  onSelect,
  minimumSize,
  hasWideNeck,
}: DynamicSizingGuideResultProps) {
  const { t } = useTranslation();
  // The recommendation is floored at the weight band, so the guide can never point below the size
  // card for this weight.
  const rows = distributionRows(sizingGuideItem);
  const distributionSize = rows.length > 0 ? rows[0].size : null;
  const sizeOption =
    distributionSize && minimumSize
      ? f3LargerSize(distributionSize as F3BandSizeOption, minimumSize as F3BandSizeOption)
      : distributionSize;
  // Say why the floor bit, and only then - a caveat shown on every result is the one shoppers learn to skip.
  const floorOverridesDistribution = !!sizeOption && !!distributionSize && sizeOption !== distributionSize;

  if (!sizeOption) return null;

  return (
    <>
      <div className={styles.sizingGuideResultContainer}>
        <div className={styles.recommendationEyebrow}>{t('we_recommend_size')}</div>
        <div className={styles.recommendationSize}>{sizeLabel(sizeOption, t)}</div>
        {floorOverridesDistribution && (
          <div className={styles.sizeUpReason}>
            <Trans
              i18nKey={
                hasWideNeck
                  ? 'breed_runs_a_size_up_we_recommend_size_rather_than_size'
                  : 'we_recommend_size_owners_who_bought_size_at_this_weight_exchanged_far_more_often'
              }
              values={{
                breed: breedName,
                recommended: sizeLabel(sizeOption, t),
                distribution: distributionSize ? sizeLabel(distributionSize, t) : '',
              }}
              components={{ rec: <span className={styles.sizeUpReasonSize} /> }}
            />
          </div>
        )}
        <button className={styles.selectSizeButton} onClick={() => onSelect(sizeOption)}>
          {t('select_size', { size: sizeLabel(sizeOption, t) })}
        </button>
      </div>
      {isPuppy && (
        <div className={styles.puppySpecialOfferSection}>
          <div className={styles.puppySpecialOfferTitle}>
            <div className={styles.pawIcon} />
            {t('fi_puppy_warranty')}
          </div>
          <div className={styles.puppySpecialOfferDescription}>
            <Trans
              i18nKey="fi_series_3_plus_is_our_most_durable_longest_lasting_wearable_get_a_size_collar_now_and_well_swap_it_out_for_free_when_they_are_fully_grown"
              values={{
                size: sizeLabel(sizeOption, t).toLowerCase(),
              }}
              components={{
                sizeSpan: <span />,
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}
